import type { 
  StAustinClient, 
  StAustinConversation, 
  ClientListFilters, 
  ClientSummary,
  ClientDetail,
  ClientState 
} from '@/types/client-management.types';

const CLIENT_MANAGEMENT_BASE_URL = 'http://localhost:3002';

class ClientManagementService {
  private async getAuthHeaders(): Promise<{ Authorization: string } | {}> {
    // Get token from session storage (where auth.service stores it)
    const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');

    if (!token) {
      console.error('No authentication token found. User might be logged out.');
      return {};
    }

    return {
      Authorization: `Bearer ${token}`
    };
  }

  private async apiRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${CLIENT_MANAGEMENT_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...options?.headers
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getClientCustomers(
    clientId: string,
    filters: ClientListFilters = {},
    pagination: { limit?: number; offset?: number } = {}
  ): Promise<{ customers: ClientSummary[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (pagination.limit) params.append('limit', pagination.limit.toString());
    if (pagination.offset) params.append('offset', pagination.offset.toString());

    return this.apiRequest<{ customers: ClientSummary[]; total: number; limit: number; offset: number }>(
      `/api/customers?${params.toString()}`
    );
  }

  async getCustomerConversations(
    customerId: string, // Now using unified lead ID (UUID)
    pagination: { limit?: number; offset?: number } = {}
  ): Promise<{ conversations: StAustinConversation[]; total: number; customerId: string }> {
    const params = new URLSearchParams();
    if (pagination.limit) params.append('limit', pagination.limit.toString());
    if (pagination.offset) params.append('offset', pagination.offset.toString());

    return this.apiRequest<{ conversations: StAustinConversation[]; total: number; customerId: string }>(
      `/api/customers/${customerId}/conversations?${params.toString()}`
    );
  }

  async checkServiceHealth(): Promise<{ status: string; service: string }> {
    // Health check disabled for production
    // TODO: Implement proper health check endpoint when backend service is available
    return { status: 'ok', service: 'client-management' };
  }
}

export const clientManagementService = new ClientManagementService();